<script lang="ts">
  /**
   * Topbar visible whenever an operator is logged in (FR-031).
   *
   * Layout: 🏭 FilAr · [zona tabs por rol] · Usuario · Perfil · Cambiar usuario
   *
   * Las "zona tabs" son los enlaces a las áreas que el rol del usuario tiene
   * permiso para entrar (unión, igual que sistema-viejo §12). El rol del JWT
   * es el de Odoo (operator / line_leader / manufacturing / supervisor /
   * admin / logistics) y manda; la `zone` de la sesión es solo un hint UX
   * de dónde aterrizó al loguearse, no restringe API.
   */
  import { goto } from "$app/navigation";
  import { page } from "$app/stores";
  import { onDestroy, onMount } from "svelte";
  import { session, clearSession } from "$lib/stores/auth";
  import { auth } from "$lib/api/auth";
  import Modal from "$lib/components/Modal.svelte";

  let confirming = $state(false);

  // A58: semáforo global de sincronización con Odoo (P4). /health/odoo existía
  // pero ningún componente lo consumía — el operario no se enteraba de que
  // Odoo estaba caído hasta que el cierre de turno le explotaba en la cara.
  type SyncColor = "green" | "amber" | "red";
  let syncColor: SyncColor = $state("green");
  let syncDetail = $state("Verificando conexión con Odoo…");
  let syncPopoverOpen = $state(false);
  let syncPollId: ReturnType<typeof setInterval> | null = null;

  // A58 (offline-first, ver docs/design/OFFLINE_FIRST.md §3.6): backlog del
  // outbox durable. /health/sync reporta cuántos writes locales todavía no se
  // replicaron a Odoo. El operario sigue trabajando offline; esto le muestra
  // cuánto falta volcar cuando vuelva la conexión.
  let outboxPending = $state(0);
  let outboxFailed = $state(0);
  let outboxOldestAgeS = $state(0);

  // Frescura de los datos de Odoo: `ref_snapshot_at` es la última vez que el
  // panel pulleó la copia durable de referencia (OTs/MOs/producibles/…). Online
  // el picker lee en vivo igual; esto le da al operario un "actualizado hace X"
  // + un botón para forzar el pull ahora (útil sobre todo antes de un corte, o
  // para confirmar que hay conexión). Ver docs/design/OFFLINE_FIRST.md §11.
  let refSnapshotAt = $state<string | null>(null);
  let refreshing = $state(false);
  let refreshError = $state(false);
  let nowTs = $state(Date.now()); // tick para recomputar el "hace X" en vivo
  let freshTickId: ReturnType<typeof setInterval> | null = null;

  const isOffline = $derived((syncColor as SyncColor) === "red");

  const lastSyncSecs = $derived(
    refSnapshotAt
      ? Math.max(0, Math.floor((nowTs - new Date(refSnapshotAt).getTime()) / 1000))
      : null,
  );
  const lastSyncLabel = $derived(
    lastSyncSecs === null
      ? "sin datos"
      : lastSyncSecs < 10
        ? "recién"
        : formatAge(lastSyncSecs),
  );
  // El cron corre cada 10 min: si pasaron >12 sin refrescar, algo va mal
  // (probablemente sin conexión) → lo marcamos en ámbar.
  const lastSyncStale = $derived(lastSyncSecs !== null && lastSyncSecs > 12 * 60);

  const SYNC_LABEL: Record<SyncColor, string> = {
    green: "Sincronizado",
    amber: "Demorado",
    red: "Sin conexión",
  };

  function formatAge(seconds: number): string {
    if (seconds < 60) return "hace segundos";
    const mins = Math.floor(seconds / 60);
    if (mins < 60) return `hace ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `hace ${hours} h`;
    const days = Math.floor(hours / 24);
    return `hace ${days} d`;
  }

  async function pollOdooHealth() {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      syncColor = "red";
      syncDetail = "Este dispositivo está sin conexión a internet.";
      return;
    }
    try {
      const resp = await fetch("/health/odoo", { credentials: "include" });
      const body = await resp.json().catch(() => ({}));
      const state = body?.breaker_state as string | undefined;
      if (state === "closed") {
        syncColor = "green";
        syncDetail = "Conectado con Odoo.";
      } else if (state === "half_open" || body?.status === "degraded") {
        syncColor = "amber";
        syncDetail = "Odoo responde lento o de forma intermitente.";
      } else {
        syncColor = "red";
        syncDetail = body?.detail ?? "Odoo no responde (circuito abierto).";
      }
    } catch {
      syncColor = "red";
      syncDetail = "No se pudo consultar el estado de Odoo (¿panel inalcanzable?).";
    }
  }

  // El backlog se sirve desde el panel local (Postgres), así que se consulta
  // aunque Odoo esté caído. Si el propio panel es inalcanzable, se dejan los
  // últimos valores conocidos (no se rompe la UI).
  async function pollBacklog() {
    try {
      const resp = await fetch("/health/sync", { credentials: "include" });
      if (!resp.ok) return;
      const body = await resp.json().catch(() => null);
      if (!body) return;
      outboxPending = Number(body.outbox_pending ?? 0);
      outboxFailed = Number(body.outbox_failed ?? 0);
      outboxOldestAgeS = Number(body.outbox_oldest_age_s ?? 0);
      refSnapshotAt = (body.ref_snapshot_at as string | null) ?? refSnapshotAt;
      nowTs = Date.now();
    } catch {
      /* panel inalcanzable → conservar el último backlog conocido */
    }
  }

  // Botón de recarga manual: fuerza el pull del snapshot de Odoo ahora.
  async function forceRefresh() {
    if (refreshing) return;
    refreshing = true;
    refreshError = false;
    try {
      const resp = await fetch("/health/sync/refresh", {
        method: "POST",
        credentials: "include",
      });
      const body = await resp.json().catch(() => null);
      if (body?.ref_snapshot_at) refSnapshotAt = body.ref_snapshot_at;
      nowTs = Date.now();
      // refrescar también el semáforo Odoo + backlog de una.
      await pollSyncHealth();
      if (body && body.refreshed === false) {
        refreshError = true; // Odoo caído: no se pudo actualizar
        setTimeout(() => (refreshError = false), 3500);
      }
    } catch {
      refreshError = true;
      setTimeout(() => (refreshError = false), 3500);
    } finally {
      refreshing = false;
    }
  }

  async function pollSyncHealth() {
    await Promise.all([pollOdooHealth(), pollBacklog()]);
  }

  onMount(() => {
    pollSyncHealth();
    syncPollId = setInterval(pollSyncHealth, 30_000);
    // Tick liviano para que el "actualizado hace X" avance sin re-pegarle al API.
    freshTickId = setInterval(() => (nowTs = Date.now()), 15_000);
    window.addEventListener("online", pollSyncHealth);
    window.addEventListener("offline", pollSyncHealth);
  });

  onDestroy(() => {
    if (syncPollId) clearInterval(syncPollId);
    if (freshTickId) clearInterval(freshTickId);
    if (typeof window !== "undefined") {
      window.removeEventListener("online", pollSyncHealth);
      window.removeEventListener("offline", pollSyncHealth);
    }
  });

  const ZONE_LABEL: Record<string, string> = {
    produccion: "Producción",
    logistica: "Logística",
    admin: "Administración",
    calidad: "Calidad",
  };

  type ZoneTab = { id: string; label: string; href: string; roles: string[] };
  const ZONE_TABS: ZoneTab[] = [
    { id: "tablero", label: "Tablero", href: "/tablero", roles: ["supervisor", "admin"] },
    { id: "produccion", label: "Producción", href: "/produccion",
      roles: ["operator", "line_leader", "maintenance", "manufacturing", "supervisor", "admin"] },
    { id: "calidad", label: "Calidad", href: "/calidad",
      roles: ["manufacturing", "supervisor", "admin"] },
    { id: "logistica", label: "Logística", href: "/logistica",
      roles: ["logistics", "supervisor", "admin"] },
    { id: "admin", label: "Admin", href: "/admin",
      roles: ["manufacturing", "supervisor", "admin"] },
  ];

  // Multi-rol: un tab es accesible si ALGUNO de los roles del usuario está en
  // la lista de roles del tab. `roles` viene del JWT (unión x_filar_role +
  // x_filar_role_ids); si una sesión vieja no lo trae, caemos a `[role]`.
  const accessibleTabs = $derived.by(() => {
    if (!$session) return [];
    const roles =
      $session.user.roles && $session.user.roles.length > 0
        ? $session.user.roles
        : [$session.user.role];
    return ZONE_TABS.filter((t) => t.roles.some((r) => roles.includes(r)));
  });

  function isActive(href: string): boolean {
    const path = $page.url.pathname;
    return path === href || path.startsWith(href + "/");
  }

  function zoneHome(z: string | null | undefined): string {
    if (z === "logistica") return "/logistica";
    if (z === "admin") return "/admin";
    if (z === "calidad") return "/calidad";
    // /produccion es el router: si hay turno abierto redirige a /produccion/shift,
    // sino muestra el formulario para abrir turno.
    return "/produccion";
  }

  async function doSwitch() {
    try {
      await auth.logout("user_switch");
    } catch {
      /* even if backend errored, we still clear locally so the UI returns */
    }
    clearSession();
    confirming = false;
    goto("/login");
  }
</script>

{#if $session}
  <header class="topbar">
    <a href={zoneHome($session.zone)} class="brand" title="Inicio">
      🏭 <span>FilAr</span>
    </a>

    {#if accessibleTabs.length > 1}
      <nav class="zone-tabs" aria-label="Zonas">
        {#each accessibleTabs as t (t.id)}
          <a href={t.href} class:active={isActive(t.href)}>{t.label}</a>
        {/each}
      </nav>
    {/if}

    <div class="info">
      <strong>{$session.user.name}</strong>
      {#if $session.zone}
        <span class="zone">· {ZONE_LABEL[$session.zone] ?? $session.zone}</span>
      {/if}
    </div>
    <nav class="topbar-actions">
      <div class="freshness" class:stale={lastSyncStale} class:err={refreshError}>
        <span class="fresh-clock" aria-hidden="true">🕒</span>
        <span class="fresh-label" title="Última sincronización con Odoo">
          {refreshError ? "sin conexión" : lastSyncLabel}
        </span>
        <button
          type="button"
          class="refresh-btn"
          class:spin={refreshing}
          onclick={forceRefresh}
          disabled={refreshing}
          aria-label="Forzar actualización desde Odoo"
          title="Forzar actualización desde Odoo"
        >↻</button>
      </div>
      <div class="sync-wrap">
        <button
          type="button"
          class="sync-badge {syncColor}"
          onclick={() => (syncPopoverOpen = !syncPopoverOpen)}
          aria-label={outboxPending > 0
            ? `Estado de sincronización: ${SYNC_LABEL[syncColor]}. ${outboxPending} pendientes de sincronizar.`
            : `Estado de sincronización: ${SYNC_LABEL[syncColor]}`}
        >
          <span class="dot"></span>
          <span class="sync-text">{SYNC_LABEL[syncColor]}</span>
          {#if outboxPending > 0}
            <span class="backlog-pill" aria-hidden="true">{outboxPending}</span>
          {/if}
        </button>
        {#if syncPopoverOpen}
          <div class="sync-popover" role="status">
            <p>{syncDetail}</p>
            {#if outboxPending > 0}
              <p class="backlog">
                <strong>{outboxPending}</strong>
                {outboxPending === 1 ? "pendiente" : "pendientes"} de sincronizar{#if outboxOldestAgeS > 0}
                  · el más antiguo {formatAge(outboxOldestAgeS)}{/if}.
              </p>
            {:else}
              <p class="backlog ok">Todo sincronizado con Odoo.</p>
            {/if}
            {#if outboxFailed > 0}
              <p class="failed">
                <strong>{outboxFailed}</strong>
                {outboxFailed === 1 ? "con error de sincronización" : "con errores de sincronización"} (se reintenta solo).
              </p>
            {/if}
            <button type="button" class="muted" onclick={() => (syncPopoverOpen = false)}>Cerrar</button>
          </div>
        {/if}
      </div>
      <a href="/profile" class="profile-link">Perfil</a>
      <button type="button" class="switch" onclick={() => (confirming = true)}>
        Cambiar usuario
      </button>
    </nav>
  </header>

  {#if isOffline}
    <div class="offline-banner" role="status" aria-live="polite">
      <span class="offline-dot"></span>
      <span class="offline-text">
        <strong>Modo offline</strong> — seguís trabajando, se sincroniza al volver internet.{#if outboxPending > 0}
          <span class="offline-count">
            {outboxPending}
            {outboxPending === 1 ? "cambio pendiente" : "cambios pendientes"}.
          </span>{/if}
      </span>
    </div>
  {/if}

  {#if confirming}
    <Modal title="¿Cerrar sesión?" onClose={() => (confirming = false)}>
      <p>Vas a cerrar la sesión de <strong>{$session.user.name}</strong>.</p>
      <div class="actions">
        <button type="button" class="muted" onclick={() => (confirming = false)}>Cancelar</button>
        <button type="button" class="danger" onclick={doSwitch}>Sí, cambiar usuario</button>
      </div>
    </Modal>
  {/if}
{/if}

<style>
  .topbar {
    position: sticky; top: 0; z-index: 5;
    display: flex; gap: 1rem; align-items: center; flex-wrap: wrap;
    padding: 0.5rem 1rem;
    background: #161922;
    border-bottom: 1px solid #2a2f3a;
    color: #e7e9ee;
  }
  .zone-tabs {
    display: flex; gap: 0.25rem; flex-wrap: wrap;
    margin-right: auto;
  }
  .zone-tabs a {
    color: #e7e9ee; text-decoration: none;
    padding: 0.4rem 0.85rem; border-radius: 8px;
    font-size: 0.9rem; font-weight: 600;
    border: 1px solid transparent;
    background: transparent;
    opacity: 0.75;
    transition: opacity 120ms, background 120ms, border-color 120ms;
  }
  .zone-tabs a:hover { background: #1d2230; opacity: 1; }
  .zone-tabs a.active {
    background: #1d2230; border-color: #4a9eff; color: #4a9eff; opacity: 1;
  }
  .brand {
    color: #4a9eff;
    text-decoration: none;
    font-size: 1.2rem;
    font-weight: 700;
    padding: 0.25rem 0.5rem;
    border-radius: 8px;
    display: flex; align-items: center; gap: 0.4rem;
  }
  .brand span { font-size: 0.95rem; }
  .brand:hover { background: #2a2f3a; }
  .info { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .info strong { font-size: 1.05rem; }
  .zone { opacity: 0.7; margin-left: 0.25rem; }
  .topbar-actions { display: flex; gap: 0.5rem; align-items: center; }
  .profile-link {
    color: #e7e9ee; text-decoration: none; opacity: 0.8;
    padding: 0.5rem 0.75rem; border-radius: 8px;
  }
  .profile-link:hover { opacity: 1; background: #2a2f3a; }
  .switch {
    background: #1d2230; color: #e7e9ee;
    border: 2px solid #4a9eff;
    border-radius: 12px;
    padding: 0.5rem 1rem;
    min-height: 44px;
    font-size: 1rem;
    cursor: pointer;
  }
  .switch:active { background: #2a2f3a; }
  .freshness {
    display: flex; align-items: center; gap: 0.35rem;
    padding: 0.25rem 0.5rem 0.25rem 0.6rem;
    border-radius: 999px;
    background: #1d2230; border: 1px solid #2a2f3a;
    color: #aeb4c2; font-size: 0.82rem; font-weight: 600;
    min-height: 44px;
  }
  .freshness.stale { color: #ffd9a3; border-color: #6b4d1f; }
  .freshness.err { color: #ff9d9d; border-color: #7a2f2f; }
  .freshness .fresh-clock { font-size: 0.85rem; opacity: 0.8; }
  .freshness .fresh-label { white-space: nowrap; }
  .refresh-btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; border-radius: 50%;
    background: transparent; border: none; cursor: pointer;
    color: #7fb3ff; font-size: 1.15rem; line-height: 1;
    transition: background 120ms;
  }
  .refresh-btn:hover:not(:disabled) { background: #2a2f3a; }
  .refresh-btn:disabled { cursor: default; opacity: 0.9; }
  .refresh-btn.spin { animation: refresh-spin 0.8s linear infinite; }
  @keyframes refresh-spin { to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) {
    .refresh-btn.spin { animation: none; }
  }
  .sync-wrap { position: relative; }
  .sync-badge {
    display: flex; align-items: center; gap: 0.4rem;
    background: #1d2230; border: 2px solid #2a2f3a;
    border-radius: 999px; padding: 0.4rem 0.85rem;
    min-height: 44px; color: #e7e9ee; font-size: 0.9rem; font-weight: 600;
    cursor: pointer;
  }
  .sync-badge .dot {
    width: 12px; height: 12px; border-radius: 50%;
    background: currentColor; display: inline-block;
  }
  .sync-badge.green { color: #4ade80; }
  .sync-badge.amber { color: #ffd9a3; }
  .sync-badge.red { color: #ff6b6b; }
  .sync-badge .sync-text { color: #e7e9ee; }
  .sync-badge .backlog-pill {
    display: inline-flex; align-items: center; justify-content: center;
    min-width: 22px; height: 22px; padding: 0 6px;
    border-radius: 999px;
    background: #f59e0b; color: #1a1205;
    font-size: 0.8rem; font-weight: 800; line-height: 1;
  }
  .sync-popover {
    position: absolute; top: calc(100% + 0.5rem); right: 0;
    background: #1d2230; border: 1px solid #2a2f3a; border-radius: 12px;
    padding: 0.85rem; width: 240px; z-index: 6;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
  }
  .sync-popover p { margin: 0 0 0.75rem; font-size: 0.9rem; opacity: 0.9; }
  .sync-popover .backlog { opacity: 1; color: #ffd9a3; }
  .sync-popover .backlog.ok { color: #4ade80; }
  .sync-popover .backlog strong,
  .sync-popover .failed strong { font-size: 1.05rem; }
  .sync-popover .failed { opacity: 1; color: #ff9d9d; }
  .sync-popover button { min-height: 40px; padding: 0.4rem 0.75rem; border-radius: 8px; border: none; cursor: pointer; }
  .actions {
    display: flex; gap: 0.75rem; justify-content: flex-end;
    margin-top: 1rem;
  }
  .actions button {
    padding: 0.75rem 1.5rem;
    border-radius: 12px;
    border: none;
    font-size: 1rem; font-weight: 600;
    cursor: pointer;
    min-height: 48px;
  }
  .muted { background: #2a2f3a; color: #e7e9ee; }
  .danger { background: #ef4444; color: #ffffff; }

  /* Banner de modo offline (P4): aparece pegado bajo el topbar sticky cuando
     Odoo/el circuito está caído; desaparece solo al volver online. No es modal:
     no bloquea el trabajo. */
  .offline-banner {
    position: sticky; top: 0; z-index: 4;
    display: flex; align-items: center; gap: 0.6rem;
    padding: 0.7rem 1rem;
    background: #3a2410;
    border-bottom: 1px solid #f59e0b;
    color: #ffe6c2;
    font-size: 0.95rem;
  }
  .offline-banner .offline-dot {
    flex: 0 0 auto;
    width: 12px; height: 12px; border-radius: 50%;
    background: #f59e0b;
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.6);
    animation: offline-pulse 2s ease-out infinite;
  }
  .offline-banner .offline-text { line-height: 1.3; }
  .offline-banner strong { color: #ffb454; }
  .offline-banner .offline-count { font-weight: 700; color: #ffd9a3; }
  @keyframes offline-pulse {
    0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.6); }
    70% { box-shadow: 0 0 0 8px rgba(245, 158, 11, 0); }
    100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
  }
  @media (prefers-reduced-motion: reduce) {
    .offline-banner .offline-dot { animation: none; }
  }
</style>
